import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { RocketIcon as Rocket, CheckCircleIcon as CheckCircle, CircleIcon as Circle, ArrowRightIcon as ArrowRight, ArrowSquareOutIcon as ArrowSquareOut, SparkleIcon as Sparkle, ScalesIcon as Scales, LightbulbIcon as Lightbulb, TrademarkIcon as Trademark, CopyrightIcon as Copyright, ShieldCheckIcon as ShieldCheck, DownloadSimpleIcon as DownloadSimple, ShareIcon as Share } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * /admin/launch-playbook — admin-only page surfacing two artefacts:
 *   1. Step-by-step launch playbook organised by phase, with per-step
 *      DONE checkbox state stored in localStorage so the founder can
 *      check things off as they ship.
 *   2. IP audit: every trademark / patent / copyright / trade-secret
 *      surface on the platform, with a recommendation + guidance.
 */
const LP_KEY = "armstrong:launch_playbook:v1";

const RECOMMEND_TONE = {
  "FILE": { color: "#0F8043", bg: "#EEFBEC" },
  "FILE (after the word mark)": { color: "#0F8043", bg: "#EEFBEC" },
  "REGISTER": { color: "#0F8043", bg: "#EEFBEC" },
  "REGISTER (compilation)": { color: "#0F8043", bg: "#EEFBEC" },
  "MAYBE": { color: "#8A6A00", bg: "#FFF8E1" },
  "MAYBE — but consider renaming first (see below)": { color: "#8A6A00", bg: "#FFF8E1" },
  "OPTIONAL": { color: "#8A6A00", bg: "#FFF8E1" },
  "PROVISIONAL ONLY": { color: "#8A6A00", bg: "#FFF8E1" },
  "NO PATENT": { color: "#6B6B6B", bg: "#F5F5F5" },
  "DO NOT FILE — generic + conflicted": { color: "#C1272D", bg: "#FDEEEE" },
  "PROTECT IN-HOUSE": { color: "#3F4A8A", bg: "#EEF0FB" },
};

const CATEGORY_ICON = {
  "Trademark (TM ®)": Trademark,
  "Patent": Lightbulb,
  "Copyright (©)": Copyright,
  "Trade Secret": ShieldCheck,
};

export default function AdminLaunchPlaybook() {
  const [data, setData] = useState(null);
  const [done, setDone] = useState({});

  useEffect(() => {
    api.get("/admin/launch-playbook/").then((r) => setData(r.data)).catch(() => {});
    try {
      const raw = localStorage.getItem(LP_KEY);
      if (raw) setDone(JSON.parse(raw));
    } catch {}
  }, []);

  const toggle = (id) => {
    const next = { ...done, [id]: !done[id] };
    setDone(next);
    try { localStorage.setItem(LP_KEY, JSON.stringify(next)); } catch {}
  };

  if (!data) {
    return (
      <AppShell>
        <p className="text-[#6B6B6B] py-12 text-center">Loading…</p>
      </AppShell>
    );
  }

  const allSteps = data.phases.flatMap((p) => p.steps);
  const doneCount = allSteps.filter((s) => done[s.id]).length;
  const progress = allSteps.length ? Math.round((doneCount / allSteps.length) * 100) : 0;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-2 md:px-0" data-testid="admin-launch-playbook">
        <div className="flex items-center gap-3 mb-3">
          <Rocket size={28} weight="duotone" className="text-crimson" />
          <h1 className="font-spectral text-4xl tracking-tight">Launch playbook &amp; IP</h1>
        </div>
        <p className="text-[14px] text-[#6B6B6B] max-w-2xl mb-6 leading-relaxed">
          Step-by-step go-to-market for maximum signups, plus a candid
          intellectual-property audit. Check off each launch step as you
          ship; state lives in your browser.
        </p>

        {/* Download / share */}
        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="lp-pdf-actions">
          <Button
            onClick={async () => {
              try {
                const r = await api.get("/admin/launch-playbook/download.pdf", { responseType: "blob" });
                const url = window.URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                const cd = r.headers["content-disposition"] || "";
                const m = cd.match(/filename="([^"]+)"/);
                a.download = m ? m[1] : "armstrong-launch-playbook.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch {
                toast.error("Couldn't download PDF — check your admin session.");
              }
            }}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="lp-download-pdf"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            Download PDF
          </Button>
          <a
            href={`mailto:?subject=${encodeURIComponent("Armstrong K9 Registry — Launch Playbook & IP Audit")}&body=${encodeURIComponent("Attached: 21-step launch playbook + intellectual-property audit (trademark / patent / copyright / trade-secret recommendations).\n\n— Founder")}`}
            className="rounded-full border border-[#C1272D] text-crimson hover:bg-[#C1272D] hover:text-white px-4 h-9 text-[12.5px] font-bold inline-flex items-center gap-1.5 transition-colors"
            data-testid="lp-email"
          >
            <Share size={12} weight="bold" /> Email
          </a>
        </div>

        {/* Progress bar */}
        <section className="rounded-2xl bg-[#0E0E0E] text-white p-6 mb-10" data-testid="lp-progress">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-gold">
              Launch progress
            </p>
            <p className="font-spectral text-3xl">
              {doneCount}<span className="text-white/40">/{allSteps.length}</span>
            </p>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#C9A227] transition-all"
              style={{ width: `${progress}%` }}
              data-testid="lp-progress-bar"
            />
          </div>
          <p className="text-[12.5px] text-white/70 mt-3">
            {progress < 50
              ? "Still in the planning + pre-launch sprint."
              : progress < 90
              ? "Mid-launch. Keep momentum — you're in the high-conversion window."
              : "Almost fully shipped. Time to start picking from the post-launch revenue levers."}
          </p>
        </section>

        {/* Phases */}
        {data.phases.map((phase) => (
          <section key={phase.label} className="mb-10" data-testid={`lp-phase-${phase.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2 flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> Phase
            </p>
            <h2 className="font-spectral text-2xl tracking-tight mb-5">{phase.label}</h2>
            <div className="space-y-3">
              {phase.steps.map((step) => {
                const isDone = !!done[step.id];
                return (
                  <article
                    key={step.id}
                    className={`rounded-xl border bg-white p-5 transition-colors ${
                      isDone
                        ? "border-[#0F8043]/40 bg-[#F6FBF6]"
                        : "border-[#E5E5E5] hover:border-[#0E0E0E]"
                    }`}
                    data-testid={`lp-step-${step.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => toggle(step.id)}
                        className="mt-0.5 flex-shrink-0"
                        aria-label={isDone ? "Mark not done" : "Mark done"}
                        data-testid={`lp-step-toggle-${step.id}`}
                      >
                        {isDone ? (
                          <CheckCircle size={22} weight="fill" className="text-[#0F8043]" />
                        ) : (
                          <Circle size={22} weight="regular" className="text-[#9CA3AF] hover:text-ink" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-[15px] ${isDone ? "text-[#5C5C5C] line-through" : "text-[#0E0E0E]"}`}>
                          {step.title}
                        </p>
                        <p className="text-[12.5px] text-[#5C5C5C] leading-relaxed mt-1.5">
                          {step.why}
                        </p>
                        <div className="flex items-center gap-4 mt-3 flex-wrap">
                          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8B8B8B]">
                            Owner · {step.owner}
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8B8B8B]">
                            Effort · {step.effort}
                          </span>
                          {step.links?.map((lnk) => (
                            lnk[1].startsWith("http") ? (
                              <a
                                key={lnk[1]}
                                href={lnk[1]}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11.5px] font-bold text-crimson hover:text-ink inline-flex items-center gap-1"
                              >
                                {lnk[0]} <ArrowSquareOut size={10} />
                              </a>
                            ) : (
                              <Link
                                key={lnk[1]}
                                to={lnk[1]}
                                className="text-[11.5px] font-bold text-crimson hover:text-ink inline-flex items-center gap-1"
                              >
                                {lnk[0]} <ArrowRight size={10} weight="bold" />
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        {/* IP audit */}
        <section className="mt-12 mb-12" data-testid="lp-ip-audit">
          <div className="flex items-center gap-3 mb-3">
            <Scales size={24} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl tracking-tight">Intellectual-property audit</h2>
          </div>
          <p className="text-[13px] text-[#6B6B6B] max-w-2xl mb-6 leading-relaxed">
            Candid view of what's worth protecting — and what isn't.
            Filing recommendations are designed to maximise legal
            protection per dollar. Not legal advice; verify with counsel.
          </p>

          {data.ip_categories.map((cat) => {
            const Icon = CATEGORY_ICON[cat.label] || Scales;
            return (
              <div key={cat.label} className="mb-8" data-testid={`lp-ip-cat-${cat.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h3 className="font-spectral text-xl tracking-tight mb-4 flex items-center gap-2.5 pb-2 border-b border-[#E5E5E5]">
                  <Icon size={18} weight="duotone" className="text-gold" />
                  {cat.label}
                </h3>
                <div className="space-y-3">
                  {cat.items.map((item) => {
                    const tone = RECOMMEND_TONE[item.recommend] || { color: "#6B6B6B", bg: "#F5F5F5" };
                    return (
                      <article
                        key={item.id}
                        className="rounded-xl border border-[#E5E5E5] bg-white p-5"
                        data-testid={`lp-ip-${item.id}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                          <p className="font-bold text-[14.5px] text-ink flex-1 min-w-0">
                            {item.asset}
                          </p>
                          <span
                            className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.16em] px-2.5 py-1 rounded-full flex-shrink-0"
                            style={{ color: tone.color, background: tone.bg }}
                          >
                            {item.recommend}
                          </span>
                        </div>
                        <p className="text-[12.5px] text-[#5C5C5C] leading-relaxed">
                          {item.guidance}
                        </p>
                      </article>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Useful filing links */}
          <div className="rounded-xl bg-[#FBF7EC] border border-[#E5D08A] p-5 text-[12.5px] text-[#5C4A00] leading-relaxed">
            <p className="font-bold text-ink mb-2">Useful filing portals</p>
            <ul className="space-y-1.5">
              <li>
                <a href="https://www.uspto.gov/trademarks/apply" target="_blank" rel="noreferrer" className="font-bold text-crimson hover:text-ink inline-flex items-center gap-1">
                  USPTO TEAS Plus (trademarks) <ArrowSquareOut size={10} />
                </a>
                {" "} — $250-350 per class.
              </li>
              <li>
                <a href="https://www.uspto.gov/patents/apply/provisional-application" target="_blank" rel="noreferrer" className="font-bold text-crimson hover:text-ink inline-flex items-center gap-1">
                  USPTO Pro Se Provisional Patent <ArrowSquareOut size={10} />
                </a>
                {" "} — $120 (small entity), 12-month priority lock, no attorney required.
              </li>
              <li>
                <a href="https://www.copyright.gov/registration/" target="_blank" rel="noreferrer" className="font-bold text-crimson hover:text-ink inline-flex items-center gap-1">
                  US Copyright Office eCO <ArrowSquareOut size={10} />
                </a>
                {" "} — $45 per work, $65 for software.
              </li>
              <li>
                <a href="https://tsdr.uspto.gov/" target="_blank" rel="noreferrer" className="font-bold text-crimson hover:text-ink inline-flex items-center gap-1">
                  TSDR — search existing trademarks <ArrowSquareOut size={10} />
                </a>
                {" "} — free; do this BEFORE filing to avoid wasted fees.
              </li>
            </ul>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
